import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const FormW2VsW9 = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="W-2 vs W-9: Differences Explained for Employers & Workers"
          description="Learn the key differences between W-2 and W-9. File accurate, IRS-compliant forms online with TaxZerone quickly, securely and accurately every time."
          keywords=""
          canonical="resources/information-returns/form-w2-vs-w9/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form W-2</span>
          </span>{' '}
          vs{' '}
          <span className="highlight-container">
            <span className="highlight">W-9</span>
          </span>{' '}
          Key Differences
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Avoid W-2 and W-9 confusion—file accurate, IRS-compliant W-2s online
          today.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=fw2vsw9`}
          >
            E-file Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                  minHeight: '100%',
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-w2-vs-w9/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form W-2 vs W-9 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <Link
                          href="/resources/information-returns/form-w-2-instructions/"
                          sx={{ color: '#0202ff' }}
                        >
                          Form W-2
                        </Link>
                        {' reports employee wages and taxes withheld, while '}
                        <Link
                          href="/resources/essential-forms/form-w-9-instructions/"
                          sx={{ color: '#0202ff' }}
                        >
                          Form W-9
                        </Link>
                        {
                          ' collects taxpayer information from contractors and vendors. '
                        }
                      </span>
                    </li>
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          Completed By:
                        </strong>
                        {
                          ' Employers complete Form W-2; contractors, vendors, or payees complete Form W-9. '
                        }
                      </span>
                    </li>
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          Who Uses It:
                        </strong>
                        {
                          ' W-2 is used for employees; W-9 is used for independent contractors and non-employee payees. '
                        }
                      </span>
                    </li>
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>When Used:</strong>
                        {
                          ' W-2 is issued annually for wage reporting; W-9 is collected before paying contractors or vendors. '
                        }
                      </span>
                    </li>
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>Filed With:</strong>
                        {' W-2 is filed with the'}
                        <strong style={{ fontWeight: 600 }}>{' SSA'}</strong>
                        {
                          ' and provided to employees; W-9 is kept by the business and not filed with the IRS. '
                        }
                      </span>
                    </li>
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          Tax Reporting:
                        </strong>
                        {
                          ' W-2 is used to file employee tax returns; W-9 information is used to prepare forms like '
                        }
                        <Link
                          href="/resources/information-returns/form-1099-nec-instructions/"
                          sx={{ color: '#0202ff' }}
                        >
                          1099-NEC.
                        </Link>{' '}
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                {/* <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 0 }}
                >
                 
                </Stack> */}

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <Link
                    href="/resources/information-returns/form-w-2-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form W-2
                  </Link>{' '}
                  and{' '}
                  <Link
                    href="/request-form-w9-online-free/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form W-9
                  </Link>{' '}
                  are both essential IRS tax forms, but they serve{' '}
                  <span style={{ fontWeight: 600 }}>
                    completely different purposes
                  </span>
                  . Form W-2 is used to report employee wages, and Form W-9 is
                  used to collect taxpayer information from independent
                  contractors and vendors. Confusing these two forms can lead to{' '}
                  <span style={{ fontWeight: 600 }}>
                    serious filing errors, IRS penalties, and payment delays
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Understanding the difference between{' '}
                  <span style={{ fontWeight: 600 }}>Form W-2 and Form W-9</span>{' '}
                  helps both businesses and workers stay compliant, report
                  income correctly, and avoid costly mistakes.
                </Typography>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents </b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#key" sx={{ color: '#0202ff' }}>
                          W-2 vs W-9: Key Differences
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Common Mistakes to Avoid
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#howdo" sx={{ color: '#0202ff' }}>
                          How do Employers Use W-2 and W-9?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#simply" sx={{ color: '#0202ff' }}>
                          Simplify your filing Form W-2 and W-9 with TaxZerone
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="key"
                >
                  W-2 vs W-9: Key differences
                </Typography>
                <Box component="div" sx={{ mb: 1 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Aspect
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 200, fontWeight: 600 }}
                          >
                            Form W-2
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Form W-9
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Purpose
                          </TableCell>

                          <TableCell>
                            Reports employee wages, tips, and taxes withheld for
                            the year
                          </TableCell>

                          <TableCell>
                            Collects payee’s legal name, address, and TIN from
                            contractors/vendors for information returns such as
                            1099{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Who completes it
                          </TableCell>

                          <TableCell>
                            Employer completes and files for each employee
                          </TableCell>

                          <TableCell>
                            Contractor, vendor, or other payee fills it out and
                            gives it to the requester{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Who receives it
                          </TableCell>

                          <TableCell>
                            Filed with SSA and copies furnished to employee{' '}
                          </TableCell>

                          <TableCell>
                            Given to the requesting business or payer; not sent
                            to IRS with the form itself
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            When it is used
                          </TableCell>

                          <TableCell>
                            At year‑end to report wages and withholdings for the
                            calendar year{' '}
                          </TableCell>

                          <TableCell>
                            Generally obtained before or when starting to pay a
                            contractor or vendor; later used to prepare
                            1099s{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Type of worker it relates to
                          </TableCell>

                          <TableCell>
                            Employees on payroll subject to federal income tax,
                            Social Security, and Medicare withholding
                          </TableCell>

                          <TableCell>
                            Independent contractors, freelancers, and certain
                            other non‑employee payees
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Key information provided
                          </TableCell>

                          <TableCell>
                            Employee and employer details, total wages, Social
                            Security and Medicare wages, federal and state tax
                            withheld, and other compensation items
                          </TableCell>

                          <TableCell>
                            Payee name, business name (if any), federal tax
                            classification, address, TIN, and relevant exemption
                            information
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Filing requirement
                          </TableCell>

                          <TableCell>
                            Must be filed with SSA and copies furnished to
                            employees by January 31 following the tax year
                          </TableCell>

                          <TableCell>
                            Kept on file by the requester; information is used
                            to complete 1099 forms and determine backup
                            withholding
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Paper vs electronic
                          </TableCell>

                          <TableCell>
                            May be filed on paper or electronically; larger
                            filers are generally required to e‑file
                          </TableCell>

                          <TableCell>
                            Not filed with IRS as a standalone form; can be
                            collected and stored electronically by the requester
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Alert
                  severity="warning"
                  icon={<EventNoteIcon />}
                  sx={{
                    mb: 4,
                    mt: 3,
                    textAlign: 'left',
                  }}
                >
                  <b>Note:</b> If the deadline falls on a weekend or federal
                  holiday, it is extended to the next business days.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="common"
                >
                  Common mistakes to avoid
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Requesting W-9 from the employee instead of W-4 </li>
                  <li>
                    {' '}
                    Failing to collect a{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      W-9 before paying a contractor
                    </span>{' '}
                  </li>
                  <li>
                    Entering{' '}
                    <span style={{ fontWeight: 600 }}>
                      incorrect SSN or EIN
                    </span>{' '}
                  </li>
                  <li>
                    Missing the{' '}
                    <Link
                      href="/resources/information-returns/form-w-2-due-dates/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      W-2 filing deadline
                    </Link>{' '}
                  </li>
                  <li>Misclassifying workers as contractors </li>
                  <li>
                    Not filing{' '}
                    <span style={{ fontWeight: 600 }}>Form W-2c</span> to
                    correct errors on a submitted W-2{' '}
                  </li>
                  <li>
                    Failing to file Form W-2c to correct errors on a submitted
                    Form W-2{' '}
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="howdo"
                >
                  How do Employers use W-2 and W-9?
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Employers request{' '}
                    <Link
                      href="/request-form-w9-online-free/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form W-9 for FREE
                    </Link>{' '}
                    from contractors and vendors to obtain their correct name,
                    address, and SSN or EIN. This information is used to prepare{' '}
                    <Link
                      href="/resources/information-returns/form-1099-nec-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 1099-NEC
                    </Link>{' '}
                    for reporting non-employee compensation.{' '}
                  </li>
                  <li>
                    Payroll data is used by employers to prepare and{' '}
                    <Link
                      href="/information-returns/efile-irs-form-w2/"
                      sx={{ color: '#0202ff' }}
                    >
                      file W-2 online
                    </Link>{' '}
                    for employees, showing their annual wages and taxes
                    withheld.{' '}
                  </li>
                </Box>

                <Alert
                  severity="info"
                  icon={false}
                  sx={{
                    mb: 4,
                    textAlign: 'left',
                  }}
                >
                  <b>Important:</b> If any errors are found on a{' '}
                  <Link
                    href="/information-returns/efile-irs-form-w2/"
                    sx={{ color: '#0202ff' }}
                  >
                    filed W2 form
                  </Link>
                  , employers must{' '}
                  <Link
                    href="/information-returns/efile-irs-form-w2-c/"
                    sx={{ color: '#0202ff' }}
                  >
                    file Form W-2c
                  </Link>{' '}
                  to correct them.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="simply"
                >
                  Simplify your filing Form W-2 and W-9 with TaxZerone
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  TaxZerone makes handling your tax forms simple and
                  stress-free. You can quickly prepare and{' '}
                  <Link
                    href="/information-returns/efile-irs-form-w2/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-file W-2 forms
                  </Link>{' '}
                  for employees and collect{' '}
                  <Link
                    href="/request-form-w9-online-free/"
                    sx={{ color: '#0202ff' }}
                  >
                    W-9 form
                  </Link>{' '}
                  <span style={{ fontWeight: 600 }}>for FREE</span> from
                  contractors and vendors. Built-in error checks help ensure
                  accurate reporting, reduce IRS rejections, and support timely
                  compliance. TaxZerone also{' '}
                  <span style={{ fontWeight: 600 }}>supports</span>{' '}
                  <Link href="/form-w2-state-filing/" sx={{ color: '#0202ff' }}>
                    state filing for Form W-2
                  </Link>
                  , helping you meet both{' '}
                  <span style={{ fontWeight: 600 }}>
                    federal and state requirements
                  </span>
                  .
                </Typography>

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The above given information is based on the IRS
                        guidelines. For more details, please refer to the IRS
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/iw2w3.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          instructions of Form W-2
                        </Link>{' '}
                        and{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/iw9.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          instructions of Form W-9
                        </Link>
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Stay Compliant, Save Time, and File with Confidence!
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              E-file your W-2s and W-9s effortlessly with TaxZerone. Start your
              free W-9 filing
              <Hidden smDown>
                {' '}
                <br />
              </Hidden>{' '}
              today and handle W-2s quickly and securely to never miss a
              deadline!
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=fw2vsw9`}
              >
                E-file Now
              </Button>
              {/* <Typography variant="body1" sx={{ mt: 1 }}>
                Takes 3 simple steps
              </Typography> */}
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default FormW2VsW9;
